import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Divider, Drawer, Group, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { contractLandInputSchema, type ContractLandInputSchema } from '../../../lib/schemas';
import { useCreateContractLand, useUpdateContractLand } from '../../../queries/contracts-land';
import { useContractLandFormLayout } from '../../../queries/contract-land-form-layout';
import { useLandLookups } from '../../../queries/land-lookups';
import { useProjectMembers } from '../../../queries/project-members';
import { useProjectStore } from '../../../project/project-store';
import { useAuthStore } from '../../../auth/auth-store';
import {
  CONTRACT_LAND_FORM_FIELDS,
  CONTRACT_LAND_FORM_FIELD_NAMES,
  CONTRACT_LAND_DEFAULT_LAYOUT,
  type ContractLandFormContext,
} from '../../../lib/form-layout/contract-land-fields';
import { FormLayoutRenderer } from '../../../lib/form-layout/FormLayoutRenderer';
import { CrossReferenceSection } from '../../../lib/CrossReferenceSection';
import { ObligationAssignmentSection } from '../../../lib/ObligationAssignmentSection';
import { ApiError } from '../../../lib/api-error';
import type { ContractLand } from '../../../types/api';

interface Props {
  opened: boolean;
  onClose: () => void;
  contract?: ContractLand | null;
  onSaved?: (c: ContractLand) => void;
}

const EMPTY: ContractLandInputSchema = {
  contract_number: '',
  contract_title: null,
  contract_type: null,
  prospect: null,
  field: null,
  reviewer_user_id: null,
  party_1: null,
  other_parties: null,
  county: null,
  state: null,
  township: null,
  range: null,
  section: null,
  block: null,
  contract_date: null,
  book: null,
  page: null,
  entry: null,
  flag_for_issues: null,
  issue_remark: null,
  description: null,
  depth_limitations: false,
  depth_remarks: null,
  royalty: false,
  orri: false,
  non_consent: false,
  non_consent_penalty: null,
  call_on_production: false,
  special_pooling_provisions: false,
  special_pooling_remarks: null,
  gas_balancing: false,
  joa_form_type: null,
  change_of_operator: null,
  remarks: null,
  reviewer_notes: null,
  change_remarks: null,
};

function fromContract(c: ContractLand): ContractLandInputSchema {
  return {
    contract_number: c.contract_number,
    contract_title: c.contract_title,
    contract_type: c.contract_type,
    prospect: c.prospect,
    field: c.field,
    reviewer_user_id: c.reviewer_user_id,
    party_1: c.party_1,
    other_parties: c.other_parties,
    county: c.county,
    state: c.state,
    township: c.township,
    range: c.range,
    section: c.section,
    block: c.block,
    contract_date: c.contract_date,
    book: c.book,
    page: c.page,
    entry: c.entry,
    flag_for_issues: c.flag_for_issues,
    issue_remark: c.issue_remark,
    description: c.description,
    depth_limitations: c.depth_limitations,
    depth_remarks: c.depth_remarks,
    royalty: c.royalty,
    orri: c.orri,
    non_consent: c.non_consent,
    non_consent_penalty: c.non_consent_penalty,
    call_on_production: c.call_on_production,
    special_pooling_provisions: c.special_pooling_provisions,
    special_pooling_remarks: c.special_pooling_remarks,
    gas_balancing: c.gas_balancing,
    joa_form_type: c.joa_form_type,
    change_of_operator: c.change_of_operator,
    remarks: c.remarks,
    reviewer_notes: c.reviewer_notes,
    change_remarks: c.change_remarks,
  };
}

const EMPTY_LOOKUPS = {
  lease_type: [],
  lease_status: [],
  lease_ownership: [],
  fed_rights: [],
  contract_type: [],
  flag_for_issues: [],
  well_status: [],
  well_type: [],
  unit_type: [],
};

export function ContractFormDrawer({ opened, onClose, contract, onSaved }: Props) {
  const isEdit = !!contract;
  const createM = useCreateContractLand();
  const updateM = useUpdateContractLand();
  const projectId = useProjectStore((s) => s.projectId);
  const role = useProjectStore((s) => s.projectRole);

  const layoutQ = useContractLandFormLayout(projectId);
  const layout = layoutQ.data?.layout ?? CONTRACT_LAND_DEFAULT_LAYOUT;

  // Fetched once at the drawer's top level (never inside the renderer's
  // per-field loop, which would break the Rules of Hooks) and threaded down
  // as the FormLayoutRenderer context — the lookup-backed Selects in
  // contract-land-fields.tsx read `context.lookups`, mirroring
  // ContractLandFormLayoutEditor's previewContext wiring.
  const { data: lookups } = useLandLookups();

  // Project members double as the reviewer_user_id dropdown's option list
  // AND the "users lookup" for resolving the display name behind
  // updated_by_user_id — /admin/users is superadmin-gated, but any project
  // member can see the membership roster.
  const { data: members } = useProjectMembers(projectId);
  const currentUser = useAuthStore((s) => s.user);
  // Server-enforced (spec §2.4): owner/superadmin may assign any project
  // member as reviewer; moderator/editor may only assign themselves. The UI
  // mirrors this to steer users away from an obvious 403, not to replace it.
  const canAssignAnyReviewer = role === 'owner' || currentUser?.system_role === 'superadmin';
  const formContext: ContractLandFormContext = {
    lookups: lookups ?? EMPTY_LOOKUPS,
    members: (members ?? []).map((m) => ({ value: m.user_id, label: m.username })),
    canAssignAnyReviewer,
    currentUserId: currentUser?.id ?? '',
  };

  const updatedByName = contract?.updated_by_user_id
    ? (members?.find((m) => m.user_id === contract.updated_by_user_id)?.username ??
      contract.updated_by_user_id)
    : null;

  const [lastOpenKey, setLastOpenKey] = useState<string | null>(null);

  const form = useForm<ContractLandInputSchema>({
    resolver: zodResolver(contractLandInputSchema),
    defaultValues: EMPTY,
  });

  const openKey = opened ? `${contract?.id ?? 'new'}-${contract?.updated_at ?? ''}` : null;
  if (openKey !== lastOpenKey) {
    setLastOpenKey(openKey);
    if (opened) form.reset(contract ? fromContract(contract) : EMPTY);
  }

  async function onSubmit(values: ContractLandInputSchema) {
    try {
      if (isEdit && contract) {
        const res = await updateM.mutateAsync({ id: contract.id, patch: values });
        notifications.show({
          title: 'Contract saved',
          message: res.contract.contract_number,
          color: 'pine',
        });
        onSaved?.(res.contract);
        onClose();
      } else {
        const res = await createM.mutateAsync(values);
        notifications.show({
          title: 'Contract created',
          message: res.contract.contract_number,
          color: 'pine',
        });
        onSaved?.(res.contract);
        onClose();
      }
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      padding="md"
      title={<Title order={4}>{isEdit ? 'Edit contract' : 'New contract'}</Title>}
      scrollAreaComponent={ScrollArea.Autosize}
      overlayProps={{ backgroundOpacity: 0.15 }}
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Stack gap="md">
          <FormLayoutRenderer
            layout={layout}
            control={form.control}
            register={form.register}
            registry={CONTRACT_LAND_FORM_FIELDS}
            allNames={CONTRACT_LAND_FORM_FIELD_NAMES}
            context={formContext}
            isEdit={isEdit}
          />

          <CrossReferenceSection entity="contract" id={contract?.id ?? null} />

          <ObligationAssignmentSection host="contract" id={contract?.id ?? null} />

          {isEdit && contract && (
            <>
              <Divider />
              <Text size="sm" c="dimmed">
                {`Last updated by ${updatedByName ?? '—'} on ${new Date(
                  contract.updated_at,
                ).toLocaleDateString()}`}
              </Text>
            </>
          )}

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" loading={form.formState.isSubmitting}>
              {isEdit ? 'Save changes' : 'Create contract'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
